import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type RowSelectionState } from "@tanstack/react-table";

import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { usePagination } from "@vega-api/custom/utils/usePagination";
import {
  getGetDealShortlistQueryKey,
  useGetDealShortlist,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { useFinaliseDealShortlist as useApproveDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist-actions/coinvest-deal-shortlist-actions";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { type ProfileResponse } from "@vega-api/generated/types/profileResponse";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { SEARCH_DEBOUNCE_DELAY_MS } from "../../../constants/searchConstants";
import { useDeleteProspect } from "../../hooks/useDeleteProspect";
import { useOpportunitiesCsvUpload } from "../../hooks/useOpportunitiesCsvUpload";
import { useProspectsCsvUpload } from "../../hooks/useProspectsCsvUpload";

const toRowSelection = (map: Record<string, unknown>): RowSelectionState =>
  Object.fromEntries(Object.keys(map).map((id) => [id, true]));

const buildUpdatedSelections = (
  newValue: RowSelectionState,
  selectedMap: Record<string, DealShortlistItemResponse>,
  currentPageData: DealShortlistItemResponse[],
): Record<string, DealShortlistItemResponse> => {
  const currentPageIds = new Set(currentPageData.map((p) => p.id));

  const selectionsFromOtherPages = Object.fromEntries(
    Object.entries(selectedMap).filter(([id]) => !currentPageIds.has(id)),
  );

  const currentPageSelections = Object.entries(newValue).reduce(
    (acc, [id, isSelected]) => {
      if (!isSelected) return acc;

      if (id in selectedMap) {
        acc[id] = selectedMap[id];

        return acc;
      }

      const prospect = currentPageData.find((p) => p.id === id);

      if (!prospect) return acc;

      acc[id] = prospect;

      return acc;
    },
    {} as Record<string, DealShortlistItemResponse>,
  );

  return { ...selectionsFromOtherPages, ...currentPageSelections };
};

const profileToApproverPerson = (profile: ProfileResponse): CoinvestPerson => ({
  id: profile.id,
  fullName: { firstName: profile.firstName, lastName: profile.lastName },
  email: profile.email,
});

export const useProspectsListManagement = () => {
  const { id: dealIdFromParams } = useParams<{ id: string }>();
  const dealId = dealIdFromParams ?? "";

  const [approvingProspectId, setApprovingProspectId] = useState<string | null>(
    null,
  );

  const {
    isOpen: isNotesModalOpen,
    setTrue: openNotesModal,
    setFalse: closeNotesModal,
  } = useBoolean(false);

  const [selectedRowIndexForNotes, setSelectedRowIndexForNotes] = useState<
    number | null
  >(null);

  const [selectedProspectsMap, setSelectedProspectsMap] = useState<
    Record<string, DealShortlistItemResponse>
  >({});
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const {
    isOpen: showApprovalModal,
    setTrue: openApprovalModal,
    setFalse: closeApprovalModal,
  } = useBoolean(false);

  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useSession();
  const paginationState = usePaginationState();

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY_MS,
  );
  const [showOnlyAddedByMe, setShowOnlyAddedByMe] = useState(false);
  const [showOnlyApproved, setShowOnlyApproved] = useState(false);

  const handleShowSelectedOnlyChange = useCallback((value: boolean) => {
    setShowSelectedOnly(value);

    if (value) {
      setShowOnlyAddedByMe(false);
      setShowOnlyApproved(false);
      setSearchQuery("");
    }
  }, []);

  const { data: shortlistData, isPending: isLoadingShortlist } =
    useGetDealShortlist(dealId, {
      isApproved: showOnlyApproved || undefined,
      wasAddedByUser: showOnlyAddedByMe || undefined,
      searchQuery: debouncedSearchQuery || undefined,
      page: paginationState.currentPage,
      pageSize: paginationState.currentPageSize,
    });
  const prospectsData: DealShortlistItemResponse[] =
    shortlistData?.data?.dealShortlistItems ?? [];

  const canAddShortlistItems = shortlistData?.data?.canAddShortlistItems;

  const canEditShortlistValues = canAddShortlistItems ?? false;

  const dataForTable = showSelectedOnly
    ? Object.values(selectedProspectsMap)
    : prospectsData;

  const { paginationElement } = usePagination({
    meta: shortlistData?.meta,
    isLoading: isLoadingShortlist,
    filterDependencies: [
      showOnlyAddedByMe,
      showOnlyApproved,
      debouncedSearchQuery,
    ],
    ...paginationState,
  });

  const rowSelection = useMemo(
    () => toRowSelection(selectedProspectsMap),
    [selectedProspectsMap],
  );

  const clearSelection = useCallback(() => {
    setSelectedProspectsMap({});
    setShowSelectedOnly(false);
  }, [setShowSelectedOnly]);

  const handleRowSelectionChange = useCallback(
    (
      updater:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      setSelectedProspectsMap((prev) => {
        const newValue =
          typeof updater === "function"
            ? updater(toRowSelection(prev))
            : updater;

        const next = buildUpdatedSelections(newValue, prev, dataForTable);

        if (Object.keys(next).length === 0) {
          setShowSelectedOnly(false);
        }

        return next;
      });
    },
    [dataForTable],
  );

  const deleteProspect = useDeleteProspect({
    dealId,
    onRemoveSuccess: (prospectId: string) => {
      setSelectedProspectsMap((prev) => {
        const { [prospectId]: _, ...rest } = prev;

        if (Object.keys(rest).length === 0) {
          setShowSelectedOnly(false);
        }

        return rest;
      });
    },
    prospectsData: dataForTable,
  });

  const prospectsCsvUpload = useProspectsCsvUpload({
    dealId,
    onUploadSuccess: clearSelection,
  });

  const opportunitiesCsvUpload = useOpportunitiesCsvUpload({
    dealId,
    onUploadSuccess: clearSelection,
  });

  const approveMutation = useApproveDealShortlist({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.coInvestments_prospectsApproved(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
      },
      onError: (error) => {
        showErrorToast(error);
      },
    },
  });

  const handleApproveProspects = () => {
    if (!dealId || !currentUser) return;

    const approvedIds = Object.keys(selectedProspectsMap);
    approveMutation.mutate(
      {
        dealId,
        data: {
          shortlistItemIds: approvedIds,
          approverPerson: profileToApproverPerson(currentUser),
        },
      },
      {
        onSuccess: () => {
          closeApprovalModal();
          clearSelection();
        },
      },
    );
  };

  const handleApproveSingleProspect = (prospectId: string) => {
    if (!dealId || !currentUser) return;

    setApprovingProspectId(prospectId);
    approveMutation.mutate(
      {
        dealId,
        data: {
          shortlistItemIds: [prospectId],
          approverPerson: profileToApproverPerson(currentUser),
        },
      },
      {
        onSettled: () => {
          setApprovingProspectId(null);
        },
      },
    );
  };

  const selectedCount = Object.keys(selectedProspectsMap).length;

  const handleOpenNotesModal = (rowIndex: number) => {
    setSelectedRowIndexForNotes(rowIndex);
    openNotesModal();
  };

  const selectedProspectNotes =
    selectedRowIndexForNotes === null
      ? undefined
      : {
          lpName: dataForTable[selectedRowIndexForNotes]?.limitedPartner.name,
          notes: dataForTable[selectedRowIndexForNotes]?.notes || "",
          shortlistItemId: dataForTable[selectedRowIndexForNotes]?.id,
        };

  return {
    prospectsData: dataForTable,
    canAddShortlistItems,
    canEditShortlistValues,
    paginationElement: showSelectedOnly ? null : paginationElement,
    searchQuery,
    setSearchQuery,
    showOnlyAddedByMe,
    setShowOnlyAddedByMe,
    showOnlyApproved,
    setShowOnlyApproved,
    showSelectedOnly,
    handleShowSelectedOnlyChange,
    isProspectsListTableLoading: isLoadingShortlist,
    isLoading:
      prospectsCsvUpload.isUploading ||
      opportunitiesCsvUpload.isUploading ||
      isLoadingShortlist ||
      deleteProspect.isDeleting ||
      approveMutation.isPending,
    rowSelection,
    selectedCount,
    showApprovalModal,
    handleApproveProspects,
    handleApproveSingleProspect,
    approvingProspectId,
    closeApprovalModal,
    openApprovalModal,
    handleRowSelectionChange,
    clearSelection,
    isNotesModalOpen,
    selectedProspectNotes,
    closeNotesModal,
    handleOpenNotesModal,
    handleProspectsFileUpload: prospectsCsvUpload.handleFileUpload,
    handleProspectsUploadClick: prospectsCsvUpload.handleUploadClick,
    handleOpportunitiesFileUpload: opportunitiesCsvUpload.handleFileUpload,
    handleOpportunitiesUploadClick: opportunitiesCsvUpload.handleUploadClick,
    ...deleteProspect,
  };
};
